import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { useAutoSave } from "@/hooks/use-auto-save";
import { useTodo, useUpdateTodo } from "@/hooks/use-todos";
import { useState } from "react";
import { toast } from "sonner";
import { TodoDetailForm } from "./TodoDetailForm";

type TodoDetailDrawerProps = {
  todoId: string | null;
  onClose: () => void;
};

/** Todo 詳細を表示・編集するボトムシート Drawer */
export const TodoDetailDrawer = ({
  todoId,
  onClose,
}: TodoDetailDrawerProps) => {
  const { data: todo, isLoading } = useTodo(todoId);
  const updateTodo = useUpdateTodo();

  const handleSave = (changes: Record<string, unknown>) => {
    if (!todoId) return;
    updateTodo.mutate(
      { id: todoId, data: changes },
      {
        onError: () => {
          toast.error("保存に失敗しました");
        },
      },
    );
  };

  return (
    <Drawer open={todoId !== null} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent aria-describedby={undefined}>
        <DrawerTitle className="sr-only">TODO詳細</DrawerTitle>
        <div className="mx-auto w-full max-w-md px-4 pb-8">
          {isLoading || !todo ? (
            <div className="flex flex-col gap-4 py-4">
              <div className="h-8 animate-pulse rounded bg-muted" />
              <div className="h-10 animate-pulse rounded bg-muted" />
              <div className="h-10 animate-pulse rounded bg-muted" />
            </div>
          ) : (
            <DrawerInner todo={todo} onSave={handleSave} />
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
};

type DrawerInnerProps = {
  todo: NonNullable<ReturnType<typeof useTodo>["data"]>;
  onSave: (changes: Record<string, unknown>) => void;
};

const DrawerInner = ({ todo, onSave }: DrawerInnerProps) => {
  const [title, setTitle] = useState(todo.title);
  const { trackChange: trackTitleChange } = useAutoSave({ onSave });

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (value.trim()) {
      trackTitleChange({ title: value });
    }
  };

  return (
    <>
      <DrawerHeader className="px-0">
        <Input
          value={title}
          onChange={(e) => handleTitleChange(e.target.value)}
          className="border-none text-lg font-semibold shadow-none focus-visible:ring-0"
        />
      </DrawerHeader>
      <TodoDetailForm todo={todo} onSave={onSave} />
    </>
  );
};
